// State parsing interface for raw HTML data
import type { AtonHost, CheckMKHost } from './useCheckMkAtonApi';

export interface ParsedHostState {
  hostName: string;
  hostState: 'UP' | 'DOWN' | 'UNREACHABLE' | 'PENDING';
  ipAddress?: string;
  serviceStates: {
    ok: number;
    warning: number;
    unknown: number;
    critical: number;
    pending: number;
  };
}

/**
 * Parse CheckMK hosts view HTML to extract state information
 */
export function parseHostStatesFromHtml(
  htmlResponse: string,
): ParsedHostState[] {
  const hostStates: ParsedHostState[] = [];

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlResponse, 'text/html');

    // Look for the data table - it has class "data table"
    const dataTable = doc.querySelector('table.data.table');

    if (!dataTable) {
      return [];
    }

    // Get all rows, but skip header and groupheader rows
    const allRows = dataTable.querySelectorAll('tr');

    // Look for rows that are NOT groupheader and have td elements (data rows)
    const dataRows = Array.from(allRows).filter((row) => {
      const hasClass =
        row.className.includes('data') ||
        (!row.className.includes('groupheader') &&
          row.querySelectorAll('td').length >= 8);
      return hasClass;
    });

    // If still no data rows, let's see what we have
    if (dataRows.length === 0) {
      return [];
    }

    dataRows.forEach((row, index) => {
      try {
        const cells = row.querySelectorAll('td');

        if (cells.length < 8) {
          return;
        }

        // Extract host state from first cell
        const stateCell = cells[0];
        const stateSpan = stateCell.querySelector('.state_rounded_fill');
        const hostState =
          (stateSpan?.textContent?.trim() as
            | 'UP'
            | 'DOWN'
            | 'UNREACHABLE'
            | 'PENDING') || 'PENDING';

        // Extract host name from second cell
        const hostCell = cells[1];
        const hostLink = hostCell.querySelector('a');
        const hostSpan = hostLink?.querySelector('.state_rounded_fill.host');
        const hostName = hostSpan?.textContent?.trim() || '';

        // Extract IP address from title attribute
        const hostTitleSpan = hostCell.querySelector('span[title]');
        const ipAddress = hostTitleSpan?.getAttribute('title') || '';

        // Extract service counts from remaining cells
        const okCount = parseInt(
          cells[3]?.querySelector('a')?.textContent?.trim() || '0',
          10,
        );
        const warningCount = parseInt(
          cells[4]?.querySelector('a')?.textContent?.trim() || '0',
          10,
        );
        const unknownCount = parseInt(
          cells[5]?.querySelector('a')?.textContent?.trim() || '0',
          10,
        );
        const criticalCount = parseInt(
          cells[6]?.querySelector('a')?.textContent?.trim() || '0',
          10,
        );
        const pendingCount = parseInt(
          cells[7]?.querySelector('a')?.textContent?.trim() || '0',
          10,
        );

        if (hostName) {
          hostStates.push({
            hostName,
            hostState,
            ipAddress,
            serviceStates: {
              ok: okCount,
              warning: warningCount,
              unknown: unknownCount,
              critical: criticalCount,
              pending: pendingCount,
            },
          });
        }
      } catch (rowError) {
        console.warn(`Error parsing host row ${index}:`, rowError);
      }
    });
  } catch (error) {
    console.error('Error parsing hosts HTML:', error);
  }

  return hostStates;
}

export const transformHostWithState = (
  checkMKHost: CheckMKHost,
  stateData?: ParsedHostState,
): AtonHost => {
  return {
    id: checkMKHost.id,
    title: checkMKHost.title,
    ipAddress: checkMKHost.extensions.attributes.ipaddress,
    folder: checkMKHost.extensions.folder,
    siteType: checkMKHost.extensions.attributes.tag_site_type,
    agent: checkMKHost.extensions.attributes.tag_agent,
    addressFamily: checkMKHost.extensions.attributes.tag_address_family,
    isCluster: checkMKHost.extensions.is_cluster,
    isOffline: checkMKHost.extensions.is_offline,
    createdAt: checkMKHost.extensions.attributes.meta_data.created_at,
    updatedAt: checkMKHost.extensions.attributes.meta_data.updated_at,
    createdBy: checkMKHost.extensions.attributes.meta_data.created_by,
    // Add state information
    hostState: stateData?.hostState || 'PENDING',
    serviceStates: stateData?.serviceStates || {
      ok: 0,
      warning: 0,
      unknown: 0,
      critical: 0,
      pending: 0,
    },
  };
};
